import { useCallback, useEffect, useState } from 'react';
import { useBatchedCallback } from '@wix/da-hooks/pkg/useBatchedCallback';
import {
  useStream,
  useNormalizedStream,
  UseStreamReturnValue,
  useFetchOnScrollNearBottom,
} from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { PapiNcMessageObjectSchema } from '../redux/normalizr/schemas';
import { NCNotification } from '../types/nc';

function useHasScrolled(): [boolean, (scrollTop: number) => void] {
  const [hasScrolled, setHasScrolled] = useState(false);

  const scrollCallback = useCallback(
    (scrollTop: number) => {
      if (scrollTop > 0 && !hasScrolled) {
        setHasScrolled(true);
      }
    },
    [hasScrolled]
  );

  return [hasScrolled, scrollCallback];
}

/**
 * Combines all the disparate scroll handlers we need for a notification feed
 */
function useFeedScrolling(streamInfo: UseStreamReturnValue<any>) {
  const [hasScrolled, hasScrolledCallback] = useHasScrolled();
  const fetchOnScrollToBottomCallback = useFetchOnScrollNearBottom(
    streamInfo,
    // Make sure you don't double fetch if you change this
    70
  );

  return {
    hasScrolled,
    onScroll: useBatchedCallback(hasScrolledCallback, fetchOnScrollToBottomCallback),
  };
}

export interface UseFeedResult
  extends
    Pick<
      UseStreamReturnValue<NCNotification>,
      'items' | 'hasMore' | 'isInitialized' | 'isFetching' | 'fetchNext' | 'errorMessage'
    >,
    Pick<ReturnType<typeof useFeedScrolling>, 'hasScrolled' | 'onScroll'> {
  isEmpty: boolean;
  mightNotBeScrollable: boolean;
  onNotScrollable: () => void;
}

/**
 * Returns everything we need to know about a feed's stream and everything we need to interact with it.
 */
function useFeedBase({
  streamInfo,
  init,
}: {
  streamInfo: UseStreamReturnValue<NCNotification>;
  init: () => void;
}): UseFeedResult {
  const { streamId, items, hasMore, fetchNext, isFetching, isInitialized, errorMessage } =
    streamInfo;
  const isEmpty = items.length === 0 && !isFetching && !hasMore && isInitialized;
  const [mightNotBeScrollable, setMightNotBeScrollable] = useState(false);
  const { hasScrolled, onScroll } = useFeedScrolling(streamInfo);

  useEffect(() => {
    if (streamId) {
      init();
    }
  }, [streamId, init]);

  const onNotScrollable = useCallback(() => {
    if (isInitialized && hasMore && !isFetching && items.length > 0) {
      setMightNotBeScrollable(true);
    }
  }, [isInitialized, hasMore, isFetching, items]);

  return {
    isEmpty,
    mightNotBeScrollable,
    hasScrolled,
    onScroll,
    onNotScrollable,
    items,
    hasMore,
    fetchNext,
    isFetching,
    isInitialized,
    errorMessage,
  };
}

/**
 * For normalized feed streams. Returns everything we need to know about a feed's stream and everything we need to interact with it.
 */
export function useNormalizedFeed(streamId: string, init: () => void) {
  const streamInfo = useNormalizedStream(streamId, [PapiNcMessageObjectSchema]);
  return useFeedBase({ streamInfo, init });
}

/**
 * For unnormalized feed streams. Returns everything we need to know about a feed's stream and everything we need to interact with it.
 */
export function useFeed(streamId: string, init: () => void) {
  const streamInfo = useStream(streamId);
  return useFeedBase({ streamInfo, init });
}
